import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { FormikValues, Field } from 'formik';
import * as Yup from 'yup';

import { Button } from '@material-ui/core';

import { adminAPI } from 'api';
import { Team } from '@types';
import { ErrorDialog } from 'components';
import { CACHE_KEYS } from 'utils/constants';
import { FormikDialog, TextInput } from 'components/Formik';

import { VIEW_MODE } from './constants';
import { ErrorContent, SharedProps } from '../types';

type Props = SharedProps & {
  team?: Team;
};

const validationSchema = Yup.object().shape({
  teamName: Yup.string().required('Required').max(255, 'Limit 255 characters').trim(),
  teamDescription: Yup.string().required('Required').max(255, 'Limit 255 characters').trim(),
});

const EditTeamDialog = ({ close, open, setViewMode, team }: Props) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<ErrorContent | null>(null);

  const { mutate: updateTeam, isLoading } = useMutation(adminAPI.patchTeam, {
    onSuccess: updatedTeam => {
      const teams = queryClient.getQueryData<Team[]>(CACHE_KEYS.teams);
      const updatedTeams = teams?.map(existingTeam =>
        existingTeam.teamId === updatedTeam.teamId
          ? { ...existingTeam, ...updatedTeam }
          : existingTeam,
      );
      queryClient.setQueryData(CACHE_KEYS.teams, updatedTeams);
      setViewMode(VIEW_MODE.viewTeams);
    },
    onError: (e: Error, { name }) => {
      if (
        e.message === `A Team already exists in the ${team?.category} category with name ${name}.`
      ) {
        setError({
          title: 'Team already exists',
          actionText: 'Got it',
          errorMessage:
            'A team with this name already exists in this vertical. Please enter a different name.',
        });
      } else {
        setError({
          title: 'Ooops! Something went wrong',
          actionText: 'Ok',
          errorMessage:
            'Sorry, we had some technical problems with your last operation. Please try again in a few minutes',
        });
      }
    },
  });

  if (!team) return null;

  const handleClose = () => setViewMode(VIEW_MODE.viewTeams);

  const formikConfig = {
    validationSchema,
    initialValues: {
      teamName: team.name,
      teamDescription: team?.description || '',
    },
    onSubmit: (values: FormikValues) =>
      updateTeam({
        teamId: team.teamId,
        description: values.teamDescription,
        name: values.teamName,
      }),
  };

  return error ? (
    <ErrorDialog {...error} open={open} close={close} onActionClick={() => setError(null)} />
  ) : (
    <FormikDialog
      formikConfig={formikConfig}
      label="edit team"
      onClose={close}
      open={open}
      title={'Edit team'}
      actions={
        <>
          <Button color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" disabled={isLoading} type="submit" variant="contained">
            Save
          </Button>
        </>
      }
    >
      <Field
        required
        fullWidth
        id="teamName"
        name="teamName"
        type="text"
        label="Team name"
        margin="normal"
        variant="outlined"
        component={TextInput}
      />
      <Field
        required
        fullWidth
        id="teamDescription"
        name="teamDescription"
        type="text"
        label="Description"
        margin="normal"
        variant="outlined"
        component={TextInput}
      />
    </FormikDialog>
  );
};

export default EditTeamDialog;
